<svelte:options accessors />

<script context="module" lang="ts">
	export let count = 0;
	const not_own_img_url =
		'https://www.frogproducts.com/wp-content/uploads/2024/05/I-do-not-own-a-pool-product.png';
</script>

<script lang="ts">
	import { productData } from './ProductSelector';
	import { fly, scale } from 'svelte/transition';
	import TextSelector from './TextSelector.svelte';
	import type { Component, ProductSelector } from '@prisma/client';
	import FormGroup from '../sub_components/FormGroup.svelte';

	export let Config: ProductSelector & Component;
	let ownPoolChkBoxEl: HTMLInputElement;
	let showOwnsError: boolean = false;
	let ownsErrorMsg: string = 'Required field';
	let showUseError: boolean = false;
	let useErrorMsg: string = 'Required field';
	let showPoolSizeError: boolean = false;
	let poolSizeErrorMsg: string = 'Required field';
	let owns: string[] = [];
	let poolProducts: string[] = [];
	let spaProducts: string[] = [];
	let swimSpaProducts: string[] = [];
	let own_frog: string | null = null;
	let pool_size: string | null = null;

	function onOwnFrogBlur(e: Event) {
		if (owns.length == 0) {
			showOwnsError = true;
		} else {
			showOwnsError = false;
		}
	}

	$: {
		if (owns.length == 0) {
			own_frog = null;
		} else {
			showOwnsError = false;
			showUseError = false;
		}
	}
	$: {
		if (own_frog === null) {
			poolProducts = [];
			spaProducts = [];
			swimSpaProducts = [];
		}
	}
	$: {
		if (!owns.includes('Pool')) {
			poolProducts = [];
			pool_size = null;
		}
	}
	$: {
		if (!owns.includes('Hot Tub')) {
			spaProducts = [];
		}
	}
	$: {
		if (!owns.includes('Swim Spa')) {
			swimSpaProducts = [];
		}
	}

	export function validate() {
		if (owns.length > 0 && own_frog === null) {
			showUseError = true;
			return false;
		} else if (owns.length == 0) {
			showOwnsError = true;
			return false;
		} else if (owns.includes('Pool') && pool_size === null && Config.GetPoolSize) {
			showPoolSizeError = true;
			return false;
		}
		return true;
	}

	export function appendData(formData: FormData) {
		formData.append(Config.DBCol + 'has_pool', owns.includes('Pool') ? 'true' : 'false');
		formData.append(Config.DBCol + 'has_hot_tub', owns.includes('Hot Tub') ? 'true' : 'false');
		formData.append(Config.DBCol + 'has_swim_spa', owns.includes('Swim Spa') ? 'true' : 'false');
		formData.append(Config.DBCol + 'uses_FROG_products', own_frog === 'Yes' ? 'true' : 'false');
		if (poolProducts.length > 0) {
			formData.append(Config.DBCol + 'pool_products', poolProducts.join(',').replaceAll('®', ''));
		}
		if (spaProducts.length > 0) {
			formData.append(Config.DBCol + 'hot_tub_products', spaProducts.join(',').replaceAll('®', ''));
		}
		if (swimSpaProducts.length > 0) {
			formData.append(
				Config.DBCol + 'swim_spa_products',
				swimSpaProducts.join(',').replaceAll('®', '')
			);
		}
		if (owns.length > 0) {
			formData.append(Config.DBCol + 'owns', owns.join(Config.DBCol + ', '));
		}
		if (typeof own_frog === 'string') {
			formData.append(Config.DBCol + 'Do you use FROG products?', own_frog);
		}
		if (typeof pool_size === 'string' && pool_size.length > 0) {
			formData.append(Config.DBCol + 'pool_size', pool_size);
		}
		let allProducts = [poolProducts, spaProducts, swimSpaProducts].flat().join(',');
		//If they don't select something, make sure MailChimp erases the values
		if (own_frog === 'No') {
			formData.append('mailchimp_erase_fields', 'MMERGE10');
		} else {
			formData.append('products', allProducts);
		}
	}

	export function setValid() {}

	export function setNuetral() {
		showUseError = false;
	}

	export function setInvalid() {
		showUseError = true;
	}

	export function focus() {
		ownPoolChkBoxEl.focus();
	}
</script>

<FormGroup {Config}>
	<div class="title2">Product Information</div>
	<div class="col" style="margin-top: 0px">
		<span class="title3" style="margin-top: 0px">
			Do you own a
			{#if Config.Required}
				<span style="color: red;">*</span>
			{/if}
		</span>
		<br />
		<div class="d-flex flex-wrap text-start">
			<div class="p-1 flex-fill" style="flex: 1 0 125px;">
				<label class="custom-select">
					<input
						type="checkbox"
						name="IOwn"
						value="Pool"
						class="form-check-input"
						on:blur={onOwnFrogBlur}
						bind:group={owns}
						bind:this={ownPoolChkBoxEl}
					/>
					<span class="checkbox" />
					Pool
				</label>
			</div>
			<div class="p-1 flex-fill" style="flex: 1 0 125px;">
				<label class="custom-select">
					<input
						type="checkbox"
						name="IOwn"
						value="Hot Tub"
						class="form-check-input"
						on:blur={onOwnFrogBlur}
						bind:group={owns}
					/>
					<span class="checkbox" />
					Hot Tub
				</label>
			</div>
			<div class="p-1 flex-fill" style="flex: 1 0 125px;">
				<label class="custom-select">
					<input
						type="checkbox"
						name="IOwn"
						value="Swim Spa"
						class="form-check-input"
						on:blur={onOwnFrogBlur}
						bind:group={owns}
					/>
					<span class="checkbox" />
					Swim Spa
				</label>
			</div>
		</div>
		{#if showOwnsError}
			<div class="invalid-msg">
				{ownsErrorMsg}
			</div>
			<div class="msg-spacer" />
		{/if}
	</div>
	{#if owns.includes('Pool') && Config.GetPoolSize}
		<span class="title3" style="margin-top: 10px; margin-bottom:2px;">
			How many gallons is your pool?
			{#if Config.Required}
				<span style="color: red;">*</span>
			{/if}
		</span>
		<div class="input-group">
			<input
				class="form-control"
				type="number"
				min="1"
				max="100000000"
				step="1"
				placeholder="Enter gallons"
				bind:value={pool_size}
				on:input={() => {
					if (!(pool_size === null || pool_size.length === 0)) {
						showPoolSizeError = false;
					}
				}}
				on:blur={() => {
					const val = parseInt(pool_size || '', 10);
					if (!isNaN(val)) {
						pool_size = val.toString();
					}
					if (pool_size) {
						if (val < 1) {
							pool_size = '1';
						} else if (val > 100000000) {
							pool_size = '100000000';
						}
					}
					showPoolSizeError = pool_size === null || pool_size.length === 0;
				}}
			/>
		</div>
		{#if showPoolSizeError}
			<div class="invalid-msg">
				{poolSizeErrorMsg}
			</div>
			<div class="msg-spacer" />
		{/if}
	{/if}
	{#if owns.length > 0 && (!owns.includes('Pool') || pool_size || !Config.GetPoolSize)}
		<span class="title3" style="margin-top: 10px; margin-bottom:2px;"
			>Do you use FROG products?
			{#if Config.Required}
				<span style="color: red;">*</span>
			{/if}</span
		>
		<div class="form-outline" transition:scale>
			<div class="custom-select-container">
				<label class="custom-select">
					<input
						type="radio"
						class="form-check-input"
						name={Config.DBCol ? Config.DBCol + '_owns' : 'owns'}
						value="Yes"
						bind:group={own_frog}
					/>
					<span class="radio" />
					Yes</label
				>
				<label class="custom-select">
					<input
						type="radio"
						class="form-check-input"
						name={Config.DBCol ? Config.DBCol + '_owns' : 'owns'}
						value="No"
						bind:group={own_frog}
					/>
					<span class="radio" />
					No</label
				>
			</div>
			{#if showUseError}
				<div class="invalid-msg">
					{useErrorMsg}
				</div>
				<div class="msg-spacer" />
			{/if}
		</div>
	{/if}
	{#if own_frog === 'Yes'}
		<div transition:scale>
			{#if owns.includes('Pool')}
				<div transition:scale>
					<div class="row g-1 align-items-stretch" style="padding-left: 0px; padding-right: 0px">
						<span class="title3" style="margin-top: 0px; margin-bottom:2px;"
							>Select the FROG Pool Products you use</span
						>
						{#each productData.PoolProducts as product}
							<div class="col-xs col-6 col-sm-4 p-1" transition:scale>
								<!-- Card with minor shadow -->
								<div
									class="card shadow-sm text-center pImage h-100 {poolProducts.includes(
										product.name
									)
										? ' image-checkbox-selected'
										: ''}"
									tabindex="0"
									role="button"
								>
									<!-- Card body to hold the label -->
									<div class="card-body" style="padding: 5px;">
										<label class="image-checkbox">
											<img class="img-fluid-p-1 img-responsive" alt="" src={product.url} />
											<input
												on:click|stopPropagation
												style="display:none"
												type="checkbox"
												name="poolProduct"
												data-ease="false"
												value={product.name}
												bind:group={poolProducts}
											/>
											{#if poolProducts.includes(product.name)}
												<i
													class="fa fa-check-circle"
													style="color: blue; background: none; font-size: 1.2em; text-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);"
												/>
											{/if}
											{product.name}
										</label>
									</div>
								</div>
							</div>
						{/each}
					</div>
				</div>
			{/if}
			{#if owns.includes('Hot Tub')}
				<div transition:scale>
					{#if owns.includes('Pool')}
						<div class="separator" />
					{/if}
					<div class="row g-1 align-items-stretch">
						<div class="title3">Select the FROG Hot Tub Products you use</div>
						{#each productData.HotTub as product}
							<div class="col-xs col-6 col-sm-4 p-1">
								<!-- Card with minor shadow -->
								<div
									class="card shadow-sm text-center pImage h-100 {spaProducts.includes(product.name)
										? ' image-checkbox-selected'
										: ''}"
									tabindex="0"
									role="button"
								>
									<!-- Card body to hold the label -->
									<div class="card-body" style="padding: 5px;">
										<label class="image-checkbox">
											<img class="img-fluid-p-1 img-responsive" alt="" src={product.url} />
											<input
												style="display:none"
												type="checkbox"
												name="poolProduct"
												data-ease="false"
												value={product.name}
												bind:group={spaProducts}
											/>
											{#if spaProducts.includes(product.name)}
												<i
													class="fa fa-check-circle"
													style="color: blue; background: none; font-size: 1.2em; text-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);"
												/>
											{/if}
											{product.name}
										</label>
									</div>
								</div>
							</div>
						{/each}
					</div>
				</div>
			{/if}
			{#if owns.includes('Swim Spa')}
				<div transition:scale>
					{#if owns.length > 1}
						<div class="separator" />
					{/if}
					<div class="row g-1 align-items-stretch">
						<div class="title3">Select the FROG Swim Spa Products you use</div>
						{#each productData.SwimSpaProducts as product}
							<div class="col-xs col-6 col-sm-4 p-1">
								<!-- Card with minor shadow -->
								<div
									class="card shadow-sm text-center pImage h-100 {swimSpaProducts.includes(
										product.name
									)
										? ' image-checkbox-selected'
										: ''}"
									tabindex="0"
									role="button"
								>
									<!-- Card body to hold the label -->
									<div class="card-body" style="padding: 5px;">
										<label class="image-checkbox">
											<img class="img-fluid-p-1 img-responsive" alt="" src={product.url} />
											<input
												style="display:none"
												type="checkbox"
												name="poolProduct"
												data-ease="false"
												value={product.name}
												bind:group={swimSpaProducts}
											/>
											{#if swimSpaProducts.includes(product.name)}
												<i
													class="fa fa-check-circle"
													style="color: blue; background: none; font-size: 1.2em; text-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);"
												/>
											{/if}
											{product.name}
										</label>
									</div>
								</div>
							</div>
						{/each}
					</div>
				</div>
			{/if}
		</div>
	{/if}
</FormGroup>

<style>
	.separator {
		width: 80%;
		height: 3px;
		background-color: #ccc;
		margin: 10px auto;
		border-top: 1px solid rgba(223, 223, 223, 0.3);
		border-bottom: 1px solid rgba(151, 151, 151, 0.3);
	}
</style>
